// src/components/ContactSection.jsx
import React from "react";
import styled from "styled-components";
import tw from "twin.macro";
import {
  FaMapMarkerAlt,
  FaPhone,
  FaFax,
  FaEnvelopeOpenText,
  FaHeadset,
  FaCity,
  FaSwimmingPool,
  FaChild,
  FaLandmark,
  FaBook,
  FaStore,
} from "react-icons/fa";
import { SiFacebook, SiX, SiYoutube, SiInstagram } from "react-icons/si";

const Section = tw.div`py-12 bg-white`;

// Top area: now 2 cols × 2 rows:
//  • Address on the left spanning both rows
//  • Social cards top‑right
//  • Other info cards bottom‑right
const InfoGrid = styled.div`
  ${tw`max-w-screen-xl mx-auto grid gap-6`}
  grid-template-columns: 1fr 2fr;
  grid-template-rows: auto auto;
  grid-template-areas:
    "address social"
    "address other";
`;

const AddressCard = styled.div`
  ${tw`flex flex-col items-center text-center justify-center p-6 bg-primary-100 rounded-lg`}
  grid-area: address;
  .icon { ${tw`text-primary-700 w-8 h-8`} }
  .title { ${tw`mt-2 text-base font-semibold`} }
  .text { ${tw`mt-3 text-sm leading-relaxed`} }
`;

// Social‑media grid (4 across)
const SocialMediaGrid = styled.div`
  ${tw`grid gap-6`}
  grid-area: social;
  grid-template-columns: repeat(4, minmax(0, 1fr));
`;

// Social card styling
const SocialCard = styled.a`
  ${tw`flex flex-col items-center text-center p-6 rounded-lg transition-transform duration-200`}
  .icon { ${tw`w-10 h-10 text-white`} }
  .title { ${tw`mt-2 text-base font-semibold text-white`} }
  &:hover { transform: scale(1.05); opacity: 0.9; }
`;


const OtherInfoGrid = styled.div`
  ${tw`grid gap-6`}
  grid-area: other;
  grid-template-columns: repeat(2, minmax(0, 1fr));
`;

const InfoCard = styled.div`
  ${tw`flex flex-col items-center text-center justify-center p-6 bg-primary-100 rounded-lg`}
  .icon { ${tw`text-primary-700 w-8 h-8`} }
  .title { ${tw`mt-2 text-base font-semibold`} }
  .text { ${tw`mt-3 text-sm leading-relaxed`} }
`;

const LinkGrid = styled.div`
  ${tw`max-w-screen-xl mx-auto grid gap-6 mt-8`}
  grid-template-columns: repeat(auto-fit, minmax(140px, 1fr));
`;

const LinkCard = styled.a`
  ${tw`flex flex-col items-center text-center p-6 bg-gray-100 rounded-lg transition-transform duration-200`}
  .icon  { ${tw`text-secondary-700 w-10 h-10`} }
  .title { ${tw`mt-2 text-base font-semibold`} }
  &:hover { transform: scale(1.05); background-color: #f3f4f6; }
`;

export default function ContactSection() {
  return (
    <Section>
      <InfoGrid>
        {/* Address */}
        <AddressCard>
          <FaMapMarkerAlt className="icon" />
          <div className="title">Adresimiz</div>
          <div className="text">
            Yenimahalle Belediye Başkanlığı<br />
            Gayret Mah. İvedik Caddesi No: 25<br />
            Yenimahalle / ANKARA
          </div>
        </AddressCard>

        {/* Social media cards */}
        <SocialMediaGrid>
          <SocialCard
            href="https://www.facebook.com/YenimahalleBelediyesi"
            target="_blank"
            rel="noopener noreferrer"
            style={{ backgroundColor: "#1877F2" }}
          >
            <SiFacebook className="icon" />
            <div className="title">Facebook</div>
          </SocialCard>
          <SocialCard
            href="https://x.com/yenimahallebld"
            target="_blank"
            rel="noopener noreferrer"
            style={{ backgroundColor: "#1DA1F2" }}
          >
            <SiX className="icon" />
            <div className="title">X</div>
          </SocialCard>
          <SocialCard
            href="https://www.youtube.com/watch?v=wYJGSYKd9rs"
            target="_blank"
            rel="noopener noreferrer"
            style={{ backgroundColor: "#FF0000" }}
          >
            <SiYoutube className="icon" />
            <div className="title">YouTube</div>
          </SocialCard>
          <SocialCard
            href="https://www.instagram.com/yenimahallebelediyesi/?hl=en"
            target="_blank"
            rel="noopener noreferrer"
            style={{
              background: "linear-gradient(45deg, #405DE6, #833AB4, #E1306C, #FD1D1D)",
            }}
          >
            <SiInstagram className="icon" />
            <div className="title">Instagram</div>
          </SocialCard>
        </SocialMediaGrid>

        {/* Other contact infos */}
        <OtherInfoGrid>
          <InfoCard>
            <FaPhone className="icon" />
            <div className="title">Telefon</div>
            <div className="text">(0312) 526 26 26</div>
          </InfoCard>
          <InfoCard>
            <FaFax className="icon" />
            <div className="title">Fax</div>
            <div className="text">(0312) 327 56 58</div>
          </InfoCard>
          <InfoCard>
            <FaEnvelopeOpenText className="icon" />
            <div className="title">KEP Adresimiz</div>
            <div className="text">
              <a
                href="mailto:yenimahallebelediyesi.yaziisleri@hs03.kep.tr"
                tw="text-primary-600 hover:underline"
              >
                yenimahallebelediyesi.yaziisleri@hs03.kep.tr
              </a>
            </div>
          </InfoCard>
          <InfoCard>
            <FaHeadset className="icon" />
            <div className="title">7/24 İletişim Merkezi</div>
            <div className="text">0 (312)870 11 11</div>
          </InfoCard>
        </OtherInfoGrid>
      </InfoGrid>

      {/* Bottom link cards */}
      <LinkGrid>
        <LinkCard href="/Icerik/semt-birimleri/48">
          <FaCity className="icon" />
          <div className="title">Semt Birimleri</div>
        </LinkCard>
        <LinkCard href="/Icerik/yuzme-havuzlari/49">
          <FaSwimmingPool className="icon" />
          <div className="title">Yüzme Havuzları</div>
        </LinkCard>
        <LinkCard href="/Icerik/kres-ve-gunduz-bakimevleri/50">
          <FaChild className="icon" />
          <div className="title">Kreş ve Gündüz Bakımevleri</div>
        </LinkCard>
        <LinkCard href="/Icerik/kultur-merkezleri/51">
          <FaLandmark className="icon" />
          <div className="title">Kültür Merkezleri</div>
        </LinkCard>
        <LinkCard href="/Icerik/kutuphanelerimiz/52">
          <FaBook className="icon" />
          <div className="title">Kütüphaneler</div>
        </LinkCard>
        <LinkCard href="/Icerik/pazar-yerleri/23">
          <FaStore className="icon" />
          <div className="title">Pazar Yerleri</div>
        </LinkCard>
      </LinkGrid>
    </Section>
  );
}
